/**
 * GroundShadowComponent — R3F component that renders the ground shadow layer.
 *
 * Three modes driven by GroundShadowManager state:
 *
 *  contact  — Drei <ContactShadows> projected from below via a secondary
 *             orthographic camera. Reacts to geometry changes (doors, hoods).
 *             Performance: renders 1 frame by default (static cars).
 *             Set frames=Infinity via setShadow({ frames: Infinity }) for animated scenes.
 *
 *  baked    — A scaled PlaneGeometry placed directly under the model with the
 *             built-in CarShadowTexture (procedural soft oval) as alphaMap.
 *             Scaled from model AABB: planeW = aabb.sizeX * m1, planeD = aabb.sizeZ * m2.
 *             Y = aabb.min.y + yOffset (tiny offset prevents z-fighting with GroundPlane).
 *             Requires setModelAabb() to have been called (done automatically in onMounted).
 *
 *  off      — Nothing rendered.
 *
 * The GroundPlane inside SceneManager is a separate geometry that only receives
 * key-light directional shadows (via shadowMaterial). This component is an
 * additional soft-shadow layer placed on top — they complement each other.
 *
 * @see docs/ims-engine-api/11-feature-lighting-shadow.md
 */

import { useState, useEffect, useMemo, useSyncExternalStore } from 'react';
import { ContactShadows, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import type { GroundShadowManager } from './GroundShadowManager';
import type { ShadowState } from './GroundShadowManager';
import type { ViewerSettingsManager } from './ViewerSettingsManager';
import { CarShadowTexture } from '../materials/textures/CarShadowTexture';

// ---------------------------------------------------------------------------
// Shadow opacity compensation for PostFX
// ---------------------------------------------------------------------------
// When the EffectComposer is active, alpha blending happens in LINEAR space
// before tone-mapping instead of in tone-mapped space.  ACES Filmic's toe
// curve lifts dark regions, making shadows appear ~30-40% lighter.
// This multiplier boosts opacity to visually match the direct-render path.
// ---------------------------------------------------------------------------
const POSTFX_SHADOW_OPACITY_BOOST = 1.4;

/** Returns true when any PostFX effect routes rendering through the EffectComposer. */
function isPostFxActive(mgr: ViewerSettingsManager | undefined): boolean {
  if (!mgr) return false;
  const s = mgr.getState();
  return s.bloom.enabled || s.antialiasing === 'smaa';
}

// ---------------------------------------------------------------------------
// TextureShadowPlane — rendered when mode === 'baked'
// ---------------------------------------------------------------------------

interface TextureShadowPlaneProps {
  state: ShadowState;
  effectiveOpacity: number;
}

// TextureShadowPlanePNG — loads shadow from a URL (useTexture caches by URL)
//
// The PNG is RGBA: alpha channel = shadow gradient (0=transparent, 1=full shadow).
// map={tex}     → load RGBA; Three.js uses the alpha channel for blending
// color="black" → multiplies output RGB by 0 → rendered area is always pure black
// transparent   → enables alpha blending from the texture's own alpha channel
// opacity       → global intensity multiplier on top of the texture alpha gradient
function TextureShadowPlanePNG({
  state,
  effectiveOpacity,
  planeW,
  planeD,
  y,
  xOffset,
  zOffset,
}: { state: ShadowState; effectiveOpacity: number; planeW: number; planeD: number; y: number; xOffset: number; zOffset: number }) {
  const tex = useTexture(state.textureUrl!);

  return (
    <mesh position={[xOffset, y, zOffset]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={1}>
      <planeGeometry args={[planeW, planeD]} />
      <meshBasicMaterial
        map={tex}
        color="black"
        transparent
        depthWrite={false}
        opacity={effectiveOpacity}
      />
    </mesh>
  );
}

// TextureShadowPlaneProc — procedural CarShadowTexture fallback
// Rendered when textureUrl is undefined (default) or explicitly set to null
// via setShadow({ mode: 'baked', textureUrl: null }).
// CarShadowTexture uses the "return canvas" pattern (same as FlakesTexture/CarbonTexture)
// so the `as unknown as HTMLCanvasElement` cast is the established contract for that class.
function TextureShadowPlaneProc({
  effectiveOpacity,
  planeW,
  planeD,
  y,
  xOffset,
  zOffset,
}: { effectiveOpacity: number; planeW: number; planeD: number; y: number; xOffset: number; zOffset: number }) {
  const shadowTexture = useMemo(
    () => new THREE.CanvasTexture(new CarShadowTexture() as unknown as HTMLCanvasElement),
    [],
  );
  useEffect(() => () => { shadowTexture.dispose(); }, [shadowTexture]);

  return (
    <mesh position={[xOffset, y, zOffset]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={1}>
      <planeGeometry args={[planeW, planeD]} />
      <meshBasicMaterial
        color="black"
        alphaMap={shadowTexture}
        transparent
        depthWrite={false}
        opacity={effectiveOpacity}
      />
    </mesh>
  );
}

function TextureShadowPlane({ state, effectiveOpacity }: TextureShadowPlaneProps) {
  const { modelAabb, m1, m2, yOffset, xOffset, zOffset } = state;

  const dims = useMemo(() => {
    if (!modelAabb) return null;
    const size = modelAabb.getSize(new THREE.Vector3());
    return {
      planeW: size.x * m1,
      planeD: size.z * m2,
      // Clamp to ground plane (y=0) so the shadow stays visible even when
      // the model is intentionally sunk below ground (groundSinkY).
      y: Math.max(modelAabb.min.y, 0) + yOffset,
    };
  }, [modelAabb, m1, m2, yOffset]);

  if (!dims) return null;
  const { planeW, planeD, y } = dims;

  if (state.textureUrl) {
    return <TextureShadowPlanePNG state={state} effectiveOpacity={effectiveOpacity} planeW={planeW} planeD={planeD} y={y} xOffset={xOffset} zOffset={zOffset} />;
  }
  return <TextureShadowPlaneProc effectiveOpacity={effectiveOpacity} planeW={planeW} planeD={planeD} y={y} xOffset={xOffset} zOffset={zOffset} />;
}

// ---------------------------------------------------------------------------
// GroundShadowComponent
// ---------------------------------------------------------------------------

interface GroundShadowComponentProps {
  manager: GroundShadowManager;
  /** Optional — when provided, shadow opacity is boosted while PostFX is active. */
  viewerSettingsManager?: ViewerSettingsManager;
}

export function GroundShadowComponent({ manager, viewerSettingsManager }: GroundShadowComponentProps) {
  const [state, setState] = useState<ShadowState>(() => manager.getState());

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional: wires manager onChange to React state updater
    manager.onChange = setState;
    return () => {
      manager.onChange = undefined;
    };
  }, [manager]);

  // Subscribe to viewer settings so we re-render when PostFX toggles change.
  useSyncExternalStore(
    (cb) => viewerSettingsManager?.subscribe(cb) ?? (() => {}),
    () => viewerSettingsManager?.version ?? 0,
  );

  const postFxActive = isPostFxActive(viewerSettingsManager);
  const effectiveOpacity = Math.min(
    state.opacity * (postFxActive ? POSTFX_SHADOW_OPACITY_BOOST : 1),
    1,
  );

  // Pre-compute contact shadow footprint from AABB (avoids per-render allocation)
  const { contactW, contactD } = useMemo(() => {
    if (!state.modelAabb) return { contactW: 6, contactD: 4 };
    const size = state.modelAabb.getSize(new THREE.Vector3());
    return { contactW: size.x * state.m1, contactD: size.z * state.m2 };
  }, [state.modelAabb, state.m1, state.m2]);

  if (state.mode === 'off') return null;

  if (state.mode === 'baked') {
    return <TextureShadowPlane state={state} effectiveOpacity={effectiveOpacity} />;
  }

  // mode === 'both': render contact shadow + baked plane together.
  // The contact shadow stays fixed at origin (x=0, z=0).
  // The baked plane uses xOffset/zOffset for independent positioning.
  // This allows e.g. a soft contact AO in the wheel arches (contact) combined
  // with a wide ground spread (baked PNG) shifted to match the model centroid.
  if (state.mode === 'both') {
    return (
      <>
        <TextureShadowPlane state={state} effectiveOpacity={effectiveOpacity} />
        <ContactShadows
          position={[0, -0.0001, 0]}
          scale={1}
          width={contactW}
          height={contactD}
          opacity={effectiveOpacity}
          blur={state.blur}
          near={0}
          far={8}
          resolution={512}
          frames={state.frames}
        />
      </>
    );
  }

  // mode === 'contact'
  //
  // ContactShadows API (drei): plane size = width * scale (both default 1/10).
  // We pass scale={1} so that width/height ARE the world-space dimensions.
  // The shadow camera looks UPWARD from the plane position; far = how many
  // units above the plane it captures. 8 units covers even tall SUVs (1.5–2m).
  //
  // Derive footprint from model AABB when available (set in onMounted);
  // fall back to a generous 6×4 m default so something is always visible.

  return (
    <ContactShadows
      position={[0, -0.0001, 0]}
      scale={1}           // IMPORTANT: keeps width/height in world units
      width={contactW}
      height={contactD}
      opacity={effectiveOpacity}
      blur={state.blur}
      near={0}            // must be 0 — near>0 clips the depth capture and produces empty FBO
      far={8}             // camera captures geometry up to 8 m above the plane
      resolution={512}
      frames={state.frames}
    />
  );
}
